import { type Minutes, minutes } from '@glory/kernel';

import type { IsoDate } from './rates.ts';

/**
 * Исключение производственного календаря. Выходные по умолчанию — суббота и воскресенье;
 * таблица хранит только отличия:
 * - `holiday` — нерабочий день (праздник или перенесённый выходной), в том числе праздник,
 *   выпавший на выходной, — ради подписи;
 * - `workday` — рабочая суббота или воскресенье (перенос);
 * - `short` — рабочий день на час короче (предпраздничный), бывает и в рабочую субботу.
 */
export const DAY_KINDS = ['holiday', 'workday', 'short'] as const;
export type DayKind = (typeof DAY_KINDS)[number];

export interface CalendarDay {
  readonly date: IsoDate;
  readonly kind: DayKind;
  readonly note: string | null;
}

/** Что за день получился: рабочий, короткий или выходной. */
export const DAY_TYPES = ['work', 'short', 'off'] as const;
export type DayType = (typeof DAY_TYPES)[number];

export type CalendarExceptions = ReadonlyMap<IsoDate, DayKind>;

/** Длина обычного рабочего дня при 40-часовой неделе и сколько снимает предпраздничный. */
export const WORKDAY_MINUTES = minutes(8 * 60);
export const SHORT_DAY_CUT = minutes(60);

export const CALENDAR_NOTE_MAX = 200;

/** Годы, которые можно смотреть и править: дальше календарь не нужен, а опечатка в годе видна сразу. */
export const CALENDAR_FIRST_YEAR = 2000;
export const CALENDAR_LAST_YEAR = 2100;

export const isCalendarYear = (year: number): boolean =>
  Number.isInteger(year) && year >= CALENDAR_FIRST_YEAR && year <= CALENDAR_LAST_YEAR;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: string): value is IsoDate {
  if (!ISO_DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** Суббота или воскресенье. */
export function isWeekend(date: IsoDate): boolean {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
}

export function dayType(date: IsoDate, exceptions: CalendarExceptions): DayType {
  const kind = exceptions.get(date);
  if (kind === 'holiday') return 'off';
  if (kind === 'short') return 'short';
  if (kind === 'workday') return 'work';
  return isWeekend(date) ? 'off' : 'work';
}

export const isWorkingDay = (date: IsoDate, exceptions: CalendarExceptions): boolean =>
  dayType(date, exceptions) !== 'off';

/** Рабочее время дня по норме 40-часовой недели. */
export function workingMinutes(date: IsoDate, exceptions: CalendarExceptions): Minutes {
  const type = dayType(date, exceptions);
  if (type === 'off') return minutes(0);
  return type === 'short' ? minutes(WORKDAY_MINUTES - SHORT_DAY_CUT) : WORKDAY_MINUTES;
}

/**
 * Какое исключение хранить, чтобы день стал нужного типа. `null` — исключение не нужно:
 * день и так такой по умолчанию. Выходной с подписью (праздник на воскресенье) хранится ради неё.
 */
export function kindFor(date: IsoDate, type: DayType, labelled: boolean): DayKind | null {
  if (type === 'short') return 'short';
  if (isWeekend(date)) {
    if (type === 'work') return 'workday';
    return labelled ? 'holiday' : null;
  }
  return type === 'off' ? 'holiday' : null;
}

/**
 * Дата через `count` рабочих дней после `from` (сам `from` не считается): шаги и сроки по календарю студии.
 * Ноль — тот же день. Исключения календаря нужны на окно от `from` на `count * 3 + 30` дней.
 */
export function addWorkingDays(from: IsoDate, count: number, exceptions: CalendarExceptions): IsoDate {
  let date = from;
  let left = Math.max(0, Math.trunc(count));
  // Предохранитель: в календаре не бывает подряд больше двух недель выходных.
  for (let guard = 0; left > 0 && guard < count * 7 + 60; guard += 1) {
    const next = new Date(`${date}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    date = next.toISOString().slice(0, 10);
    if (isWorkingDay(date, exceptions)) left -= 1;
  }
  return date;
}

/** Все даты месяца (`month` — 1…12). */
export function monthDates(year: number, month: number): IsoDate[] {
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Array.from(
    { length: days },
    (_, i) => `${String(year)}-${String(month).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`,
  );
}

export interface MonthNorm {
  readonly month: number;
  readonly workingDays: number;
  readonly daysOff: number;
  /** Норма рабочего времени при 40-часовой неделе. */
  readonly minutes: Minutes;
}

/** Нормы по месяцам года: рабочие дни, выходные, часы при 40-часовой неделе. */
export function yearNorms(year: number, exceptions: CalendarExceptions): MonthNorm[] {
  return Array.from({ length: 12 }, (_, i) => {
    const dates = monthDates(year, i + 1);
    const working = dates.filter((date) => isWorkingDay(date, exceptions));
    return {
      month: i + 1,
      workingDays: working.length,
      daysOff: dates.length - working.length,
      minutes: minutes(working.reduce((sum, date) => sum + workingMinutes(date, exceptions), 0)),
    };
  });
}

export const exceptionsOf = (days: readonly Pick<CalendarDay, 'date' | 'kind'>[]): CalendarExceptions =>
  new Map(days.map((day) => [day.date, day.kind]));

/** Год календаря как объект журнала: у дня нет своего id, история ведётся по году (UUID версии 8). */
export function calendarEntityId(year: number): string {
  return `00000000-0000-8000-8000-${String(year).padStart(12, '0')}`;
}
